import { useState, useCallback, useEffect, useRef } from 'react';
import { PLAYER_COLORS } from '../shared/constants';

const useGameGraphics = () => {
  // Image assets state
  const [destructibleWallImage, setDestructibleWallImage] = useState(null);
  const [indestructibleWallImage, setIndestructibleWallImage] = useState(null);
  const [floorImage, setFloorImage] = useState(null);
  const [bombImage, setBombImage] = useState(null);
  const [bombSVGText, setBombSVGText] = useState('');
  
  const [fireCenterImage, setFireCenterImage] = useState(null);
  const [fireLineImage, setFireLineImage] = useState(null);
  const [fireEndImage, setFireEndImage] = useState(null);
  
  // Character sprite assets
  const [characterSprites, setCharacterSprites] = useState({});
  const [characterSpriteSVGs, setCharacterSpriteSVGs] = useState({});
  const [svgSpritesLoaded, setSvgSpritesLoaded] = useState(false);
  
  // Sprite caches
  const coloredSpritesCache = useRef(new Map()); // Cache for colored sprites: "color-direction-frame" -> colored image
  const coloredBombCache = useRef(new Map()); // Cache for colored bombs: "bomb-playerId" -> colored image

  // Function to create a colored version of a sprite from SVG text
  const createColoredSprite = useCallback((svgText, playerColor) => {
    if (!svgText) return null;
    
    // Create a copy of the SVG text
    let coloredSVG = svgText;
    
    // Parse the color to ensure we have a valid color string
    let colorString = playerColor;
    if (!playerColor.startsWith('#') && !playerColor.startsWith('rgb')) {
      // Convert color names to hex or handle server colors
      const colorMap = {
        'red': PLAYER_COLORS[1],
        'blue': PLAYER_COLORS[0],
        'green': PLAYER_COLORS[2],
        'yellow': PLAYER_COLORS[3],
        [PLAYER_COLORS[0]]: PLAYER_COLORS[0], // Server blue
        [PLAYER_COLORS[1]]: PLAYER_COLORS[1], // Server red  
        [PLAYER_COLORS[2]]: PLAYER_COLORS[2], // Server green
        [PLAYER_COLORS[3]]: PLAYER_COLORS[3]  // Server yellow
      };
      colorString = colorMap[playerColor] || '#808080';
    }
    
    // Replace colors in SVG using various patterns to catch different SVG formats
    
    // Pattern 1: Replace style attribute fill colors for shirt and pants
    coloredSVG = coloredSVG.replace(/id="shirt"[^>]*style="[^"]*fill:[^;"]*[^"]*"/g, (match) => {
      return match.replace(/fill:[^;"]*/, `fill:${colorString}`);
    });
    
    coloredSVG = coloredSVG.replace(/id="pants"[^>]*style="[^"]*fill:[^;"]*[^"]*"/g, (match) => {
      return match.replace(/fill:[^;"]*/, `fill:${colorString}`);
    });
    
    // Pattern 2: Replace direct fill attribute for shirt and pants
    coloredSVG = coloredSVG.replace(/id="shirt"[^>]*fill="[^"]*"/g, (match) => {
      return match.replace(/fill="[^"]*"/, `fill="${colorString}"`);
    });
    
    coloredSVG = coloredSVG.replace(/id="pants"[^>]*fill="[^"]*"/g, (match) => {
      return match.replace(/fill="[^"]*"/, `fill="${colorString}"`);
    });
    
    // Create a blob URL from the modified SVG
    const blob = new Blob([coloredSVG], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    
    // Create an image element with the colored SVG
    const coloredImage = new Image();
    coloredImage.src = url;
    
    // Clean up blob URL when image loads (to prevent memory leaks)
    coloredImage.onload = () => {
      URL.revokeObjectURL(url);
    };
    
    return coloredImage;
  }, []);

  // Function to create a colored bomb sprite
  const createColoredBomb = useCallback((svgText, playerColor) => {
    if (!svgText) return null;
    
    // Create a copy of the SVG text
    let coloredSVG = svgText;
    
    // Parse the color to ensure we have a valid color string
    let colorString = playerColor;
    if (!playerColor.startsWith('#') && !playerColor.startsWith('rgb')) {
      const colorMap = {
        'red': PLAYER_COLORS[1],
        'blue': PLAYER_COLORS[0],
        'green': PLAYER_COLORS[2],
        'yellow': PLAYER_COLORS[3],
        [PLAYER_COLORS[0]]: PLAYER_COLORS[0],
        [PLAYER_COLORS[1]]: PLAYER_COLORS[1],  
        [PLAYER_COLORS[2]]: PLAYER_COLORS[2],
        [PLAYER_COLORS[3]]: PLAYER_COLORS[3]
      };
      colorString = colorMap[playerColor] || '#333333';
    }
    
    // Replace bomb body color - target the actual bomb SVG colors
    coloredSVG = coloredSVG.replace(/fill="#1a1a1a"|fill="#333333"|fill="#000000"|fill="black"/g, `fill="${colorString}"`);
    
    // Create a blob URL from the modified SVG
    const blob = new Blob([coloredSVG], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    
    // Create an image element with the colored SVG
    const coloredImage = new Image();
    coloredImage.src = url;
    
    // Clean up blob URL when image loads
    coloredImage.onload = () => {
      URL.revokeObjectURL(url);
    };
    
    return coloredImage;
  }, []);

  // Load all image assets
  useEffect(() => {
    const loadImages = async () => {
      
      // Load destructible wall image
      const destructibleWall = new Image();
      destructibleWall.src = `${process.env.PUBLIC_URL}/sprites/des-wall.svg`;
      destructibleWall.onload = () => {
        setDestructibleWallImage(destructibleWall);
      };
      destructibleWall.onerror = () => {
        console.warn('Failed to load destructible wall image');
      };
      
      // Load indestructible wall image
      const indestructibleWall = new Image();
      indestructibleWall.src = `${process.env.PUBLIC_URL}/sprites/ind-wall.svg`;
      indestructibleWall.onload = () => {
        setIndestructibleWallImage(indestructibleWall);
      };
      indestructibleWall.onerror = () => {
        console.warn('Failed to load indestructible wall image');
      };
      
      // Load floor image
      const floor = new Image();
      floor.src = `${process.env.PUBLIC_URL}/sprites/floor.svg`;
      floor.onload = () => {
        setFloorImage(floor);
      };
      floor.onerror = () => {
        console.warn('Failed to load floor image');
      };
      
      // Load bomb image and SVG text
      const bomb = new Image();
      bomb.src = `${process.env.PUBLIC_URL}/sprites/bomb.svg`;
      bomb.onload = () => {
        setBombImage(bomb);
      };
      bomb.onerror = () => {
        console.warn('Failed to load bomb image');
      };

      // Load bomb SVG text for recoloring
      fetch(`${process.env.PUBLIC_URL}/sprites/bomb.svg`)
        .then(response => response.text())
        .then(svgText => {
          setBombSVGText(svgText);
        })
        .catch(error => {
          console.warn('Failed to load bomb SVG text:', error);
        });
      
      // Load fire center image
      const fireCenter = new Image();
      fireCenter.src = `${process.env.PUBLIC_URL}/fire-center.svg`;
      fireCenter.onload = () => {
        setFireCenterImage(fireCenter);
      };
      fireCenter.onerror = () => {
        console.warn('Failed to load fire center image');
      };
      
      // Load fire line image
      const fireLine = new Image();
      fireLine.src = `${process.env.PUBLIC_URL}/fire-line.svg`;
      fireLine.onload = () => {
        setFireLineImage(fireLine);
      };
      fireLine.onerror = () => {
        console.warn('Failed to load fire line image');
      };
      
      // Load fire end image
      const fireEnd = new Image();
      fireEnd.src = `${process.env.PUBLIC_URL}/fire-end.svg`;
      fireEnd.onload = () => {
        setFireEndImage(fireEnd);
      };
      fireEnd.onerror = () => {
        console.warn('Failed to load fire end image');
      };
      
      // Load character sprites asynchronously
      const directions = ['front', 'back', 'left', 'right'];
      const frameCount = 4; // 4 frames per direction (0, 1, 2, 3)
      
      const sprites = {};
      const spriteSVGs = {};
      
      for (const direction of directions) {
        sprites[direction] = [];
        spriteSVGs[direction] = [];
        
        for (let frame = 0; frame < frameCount; frame++) {
          const imagePath = `${process.env.PUBLIC_URL}/sprites/character/${direction}_${frame}.svg`;
          
          // Load the image
          const img = new Image();
          img.src = imagePath;
          img.onload = () => {
            sprites[direction][frame] = img;
            setCharacterSprites(prev => ({ ...prev, ...sprites }));
          };
          img.onerror = () => {
            console.warn(`Failed to load character sprite: ${imagePath}`);
          };
          
          // Load the SVG text for recoloring
          fetch(imagePath)
            .then(response => response.text())
            .then(svgText => {
              spriteSVGs[direction][frame] = svgText;
              setCharacterSpriteSVGs(prev => ({ ...prev, ...spriteSVGs }));
              
              // Check if all sprites are loaded
              const totalSprites = directions.length * frameCount;
              const loadedCount = Object.values(spriteSVGs).reduce((acc, dir) => 
                acc + Object.values(dir).filter(sprite => sprite).length, 0
              );
              
              if (loadedCount === totalSprites) {
                setSvgSpritesLoaded(true);
              }
            })
            .catch(error => {
              console.warn(`Failed to load character sprite SVG: ${imagePath}`, error);
            });
        }
      }
    };
    
    loadImages();
  }, []);

  return {
    // Image assets
    destructibleWallImage,
    indestructibleWallImage,
    floorImage,
    bombImage,
    bombSVGText,
    fireCenterImage,
    fireLineImage,
    fireEndImage,
    
    // Character sprites
    characterSprites,
    characterSpriteSVGs,
    svgSpritesLoaded,
    
    // Sprite caches
    coloredSpritesCache,
    coloredBombCache,
    
    // Functions
    createColoredSprite,
    createColoredBomb
  };
};

export default useGameGraphics;